import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, User, Calendar, Check, AlertTriangle, Trophy } from "lucide-react";

const PILL = (color) => {
  switch (color) {
    case "red": case "orange": case "amber": return { bg: "bg-warn-soft", text: "text-warn", border: "border-warn/40" };
    case "light_green": case "dark_green": return { bg: "bg-good-soft", text: "text-good", border: "border-good/40" };
    default: return { bg: "bg-secondary", text: "text-ink-muted", border: "border-ink-line" };
  }
};
const BAR = (color) => (color === "red" || color === "orange" || color === "amber") ? "bg-warn"
  : (color === "light_green" || color === "dark_green") ? "bg-good" : "bg-brand";

export default function PcabDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [schema, setSchema] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: d }, { data: sch }] = await Promise.all([
          api.get(`/pcab/submissions/${id}`),
          api.get(`/pcab/schema`),
        ]);
        setData(d); setSchema(sch);
      } catch (ex) {
        toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      }
    })();
  }, [id]);

  if (!data || !schema) return <div className="text-sm text-ink-muted uppercase tracking-wider">Loading…</div>;
  const headlinePill = PILL(data.tier_color);

  return (
    <div className="space-y-6" data-testid="pcab-detail-page">
      <button onClick={() => nav(-1)} className="text-[12px] uppercase tracking-wider text-ink-muted hover:text-ink flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to PCAB submissions
      </button>

      <div className="border-b border-ink-line pb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">PCAB Assessment</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">{data.user_name || data.user_email || "—"}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-ink-muted">
            <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {data.user_email || "—"}</span>
            <span className="flex items-center gap-1.5 font-num"><Calendar className="w-3.5 h-3.5" /> {data.completed_at ? new Date(data.completed_at).toLocaleString() : (data.updated_at ? new Date(data.updated_at).toLocaleString() : "—")}</span>
            <span className="text-xs uppercase tracking-wider">{data.status}</span>
          </div>
        </div>
      </div>

      {/* Headline */}
      <div className={`card-flat p-7 border-l-4 ${headlinePill.border}`}>
        <div className="flex items-baseline gap-2">
          <div className="font-num text-7xl font-bold leading-none">{data.total_score}</div>
          <div className="text-2xl font-num text-ink-muted">/ 100</div>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className={`px-3 py-1 text-sm font-display font-bold rounded-sm border ${headlinePill.bg} ${headlinePill.text} ${headlinePill.border}`}>{data.tier_label || "—"}</span>
        </div>
        <div className="mt-3 text-xs text-ink-muted font-num">{data.answered} / {data.total_questions} answered</div>
      </div>

      {/* Pillar breakdown */}
      <div className="card-flat p-6">
        <div className="stat-label">Pillar breakdown</div>
        <div className="space-y-3 mt-4">
          {(data.category_breakdown || []).map((c) => {
            const pct = Math.round((c.score / c.max_score) * 100);
            const flagPill = PILL(c.flag_color);
            return (
              <div key={c.id} className="space-y-1">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{c.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-num text-ink-muted">{c.score} / {c.max_score}</span>
                    <span className={`chip text-[10px] ${flagPill.bg} ${flagPill.text} ${flagPill.border}`}>{c.flag_label}</span>
                  </span>
                </div>
                <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                  <div className={`h-full transition-all ${BAR(c.flag_color)}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {data.gaps && data.gaps.length > 0 && (
        <div className="card-flat p-6">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-warn" />
            <div className="stat-label">Capability Gaps · {data.gaps.length} identified</div>
          </div>
          <div className="space-y-4 mt-4">
            {data.gaps.map((g) => (
              <div key={g.id} className="p-4 rounded-sm border border-warn/40 bg-warn-soft">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="font-display font-bold text-sm">{g.label}</div>
                  <div className="font-num text-sm font-bold text-warn">{g.score} / {g.max_score}</div>
                </div>
                <div className="text-xs text-ink mt-3 italic leading-relaxed">{g.action}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.strengths && data.strengths.length > 0 && (
        <div className="card-flat p-6">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-good" />
            <div className="stat-label">Capability Strengths · {data.strengths.length} identified</div>
          </div>
          <div className="space-y-4 mt-4">
            {data.strengths.map((s) => (
              <div key={s.id} className="p-4 rounded-sm border border-good/40 bg-good-soft">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="font-display font-bold text-sm">{s.label}</div>
                  <div className="font-num text-sm font-bold text-good">{s.score} / {s.max_score}</div>
                </div>
                <div className="text-xs text-ink mt-3 leading-relaxed">{s.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All 25 questions with selected option */}
      <div className="card-flat" data-testid="pcab-detail-answers">
        <div className="px-6 py-4 border-b border-ink-line">
          <div className="stat-label">Question-by-question detail</div>
          <div className="font-display font-bold text-lg tracking-tight mt-1">All 25 answers</div>
        </div>
        <div className="divide-y divide-ink-line">
          {schema.questions.map((q) => {
            const selectedIdx = data.answers?.[q.code];
            const cat = schema.categories.find((c) => c.id === q.category);
            return (
              <div key={q.code} className="px-6 py-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                  {cat?.label} · {q.code}
                </div>
                <div className="text-sm font-medium mb-3">{q.label}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {q.options.map((o, i) => {
                    const isSel = selectedIdx === i;
                    return (
                      <div key={i} className={`flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-sm border ${isSel ? "border-brand bg-brand/5 text-ink" : "border-ink-line bg-white text-ink-muted"}`}>
                        <span className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${isSel ? "border-brand bg-brand text-white" : "border-ink-line"}`}>
                            {isSel && <Check className="w-3 h-3" />}
                          </span>
                          {o.label}
                        </span>
                        <span className={`font-num text-xs ${isSel ? "text-brand font-bold" : "text-ink-muted"}`}>{o.score}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
